import { useEffect, useState, type FormEvent } from "react";
import { Building2, Eye, KeyRound, MapPin, Pencil, Plus, Power, RotateCcw, Search, Trash2, UserPlus } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Dialog, Field, Input, Select } from "@/components/ui/primitives";
import { ConfirmButton } from "@/components/ui/ConfirmButton";
import { RequirePermission } from "@/features/auth/RequirePermission";
import { useAuth } from "@/features/auth/auth-context";
import { formatDateTime } from "@/lib/utils";
import { useUsers } from "@/features/users/hooks";
import { useSites } from "@/features/sites/hooks";
import { SERVICE_CONTRACT_TYPE_LABELS, SERVICE_STATUS_LABELS } from "./api";
import type {
  CustomerLocation,
  PortalUserCreated,
  ServiceContractType,
  ServiceStatus,
} from "./api";
import { amcBadge } from "./amc";
import {
  useAddContact,
  useAddLocation,
  useCreateCustomer,
  useCreatePortalUser,
  useCustomer,
  useCustomers,
  useDeleteContact,
  useDeleteCustomer,
  useDeletePortalUser,
  usePortalUsers,
  useResetPortalUserPassword,
  useUpdateCustomer,
  useUpdatePortalUser,
} from "./hooks";

const PAGE_SIZE = 10;

export default function CustomersPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [amcFilter, setAmcFilter] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const filters = {
    page,
    page_size: PAGE_SIZE,
    search: search.trim(),
    ...(amcFilter ? { amc_expiring_days: Number(amcFilter) } : {}),
  };

  const { data, isLoading, isError, error } = useCustomers(filters);
  const remove = useDeleteCustomer();
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  async function run(fn: () => Promise<unknown>) {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Companies, sites, contacts and AMC contracts.
          </p>
        </div>
        <RequirePermission permission="customer:create">
          <Button
            onClick={() => {
              setEditingId(null);
              setFormOpen(true);
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add customer
          </Button>
        </RequirePermission>
      </div>

      {actionError && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {actionError}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            aria-label="Search customers"
            placeholder="Search company, code, city or GSTIN"
            className="pl-8"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <Select
          aria-label="Filter by AMC expiry"
          className="w-auto min-w-[190px]"
          value={amcFilter}
          onChange={(e) => {
            setAmcFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All AMC statuses</option>
          <option value="0">Already expired</option>
          <option value="30">Expiring within 30 days</option>
          <option value="90">Expiring within 90 days</option>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 font-medium">Company</th>
              <th className="px-4 py-2.5 font-medium">GSTIN</th>
              <th className="px-4 py-2.5 font-medium">Sites</th>
              <th className="px-4 py-2.5 font-medium">Account manager</th>
              <th className="px-4 py-2.5 font-medium">Service</th>
              <th className="px-4 py-2.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-destructive">
                  {error instanceof ApiError ? error.message : "Could not load customers."}
                </td>
              </tr>
            )}
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  No customers match these filters.
                </td>
              </tr>
            )}
            {data?.items.map((c) => {
              const amc = amcBadge(c.amc_status, c.days_to_amc_expiry);
              // Combined service coverage: status + type, e.g. "Active · AMC" or
              // "Active · Paid Service". Tone follows the service status.
              const svcType = c.service_contract_type
                ? SERVICE_CONTRACT_TYPE_LABELS[c.service_contract_type]
                : null;
              const svcStatus = c.service_status
                ? SERVICE_STATUS_LABELS[c.service_status]
                : null;
              const svcLabel = [svcStatus, svcType].filter(Boolean).join(" · ");
              const svcTone =
                c.service_status === "active"
                  ? "success"
                  : c.service_status === "expired"
                    ? "danger"
                    : "muted";
              return (
                <tr key={c.id} className="border-t border-border">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setDetailId(c.id)}
                      className="text-left font-medium hover:underline"
                    >
                      {c.company_name}
                    </button>
                    <div className="text-xs text-muted-foreground">
                      {c.code}
                      {c.site_name ? ` · ${c.site_name}` : ""}
                      {c.city ? ` · ${c.city}` : ""}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{c.gst_number ?? "—"}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setDetailId(c.id)}
                      title="View / add offices"
                      className="inline-flex items-center gap-1 rounded px-1 text-muted-foreground hover:text-foreground hover:underline"
                    >
                      <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                      {c.location_count} office{c.location_count === 1 ? "" : "s"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {c.account_manager_name ?? "Unassigned"}
                  </td>
                  <td className="px-4 py-3">
                    {svcLabel ? (
                      <Badge tone={svcTone}>{svcLabel}</Badge>
                    ) : (
                      <Badge tone={amc.tone}>{amc.label}</Badge>
                    )}
                    {/* AMC expiry countdown as a secondary line (only when the
                        primary badge is the service label, so it's not repeated). */}
                    {svcLabel && c.amc_expiry_date && (
                      <div className="mt-0.5 text-xs text-muted-foreground">{amc.label}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" onClick={() => setDetailId(c.id)}>
                        View
                      </Button>
                      <RequirePermission permission="customer:update">
                        <Button
                          variant="ghost"
                          aria-label={`Edit ${c.company_name}`}
                          onClick={() => {
                            setEditingId(c.id);
                            setFormOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </RequirePermission>
                      <RequirePermission permission="customer:delete">
                        <ConfirmButton
                          aria-label={`Remove ${c.company_name}`}
                          title="Delete customer?"
                          message={
                            <>Remove <span className="font-medium text-foreground">{c.company_name}</span>? This deactivates and removes the customer.</>
                          }
                          onConfirm={() => run(() => remove.mutateAsync(c.id))}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </ConfirmButton>
                      </RequirePermission>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{data ? `${data.total} customer${data.total === 1 ? "" : "s"}` : ""}</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      <CustomerFormDialog
        open={formOpen}
        editingId={editingId}
        onClose={() => {
          setFormOpen(false);
          setEditingId(null);
        }}
      />
      <CustomerDetailDialog customerId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

const EMPTY_CUSTOMER_FORM = {
  company_name: "",
  site_id: "",
  contact_name: "",
  city: "",
  email: "",
  phone: "",
  gst_number: "",
  amc_expiry_date: "",
  account_manager_id: "",
  service_contract_type: "",
  service_contract_start_date: "",
  service_contract_expiry_date: "",
  service_status: "",
};

function CustomerFormDialog({
  open,
  onClose,
  editingId,
}: {
  open: boolean;
  onClose: () => void;
  editingId?: string | null;
}) {
  const create = useCreateCustomer();
  const update = useUpdateCustomer();
  const { can } = useAuth();
  const { data: editing } = useCustomer(open && editingId ? editingId : null);
  // Account managers are staff; the picker needs the user list (user:read).
  const canReadUsers = can("user:read");
  const { data: staffPage } = useUsers({ page_size: 100 }, { enabled: open && canReadUsers });
  const { data: sites } = useSites({ enabled: open });
  const managers = (staffPage?.items ?? []).filter(
    (u) => u.role_code !== "customer" && u.is_active,
  );
  const [form, setForm] = useState(EMPTY_CUSTOMER_FORM);
  const [error, setError] = useState<string | null>(null);

  // Prefill when editing (once the detail loads); reset for a fresh create.
  useEffect(() => {
    if (!open) return;
    if (editingId && editing) {
      setForm({
        company_name: editing.company_name,
        site_id: editing.site_id ?? "",
        contact_name: editing.contact_name ?? "",
        city: editing.city ?? "",
        email: editing.email ?? "",
        phone: editing.phone ?? "",
        gst_number: editing.gst_number ?? "",
        amc_expiry_date: editing.amc_expiry_date ?? "",
        account_manager_id: editing.account_manager_id ?? "",
        service_contract_type: editing.service_contract_type ?? "",
        service_contract_start_date: editing.service_contract_start_date ?? "",
        service_contract_expiry_date: editing.service_contract_expiry_date ?? "",
        service_status: editing.service_status ?? "",
      });
    } else if (!editingId) {
      setForm(EMPTY_CUSTOMER_FORM);
    }
  }, [open, editingId, editing]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const body = {
      company_name: form.company_name.trim(),
      site_id: form.site_id || null,
      contact_name: form.contact_name.trim() || null,
      city: form.city || null,
      email: form.email || null,
      phone: form.phone || null,
      // GSTIN is checksum-validated server-side; sending "" would fail
      // validation rather than mean "not provided".
      gst_number: form.gst_number.trim() || null,
      amc_expiry_date: form.amc_expiry_date || null,
      account_manager_id: form.account_manager_id || null,
      service_contract_type:
        (form.service_contract_type || null) as ServiceContractType | null,
      service_contract_start_date: form.service_contract_start_date || null,
      service_contract_expiry_date: form.service_contract_expiry_date || null,
      service_status: (form.service_status || null) as ServiceStatus | null,
    };
    try {
      if (editingId) {
        await update.mutateAsync({ id: editingId, body });
      } else {
        await create.mutateAsync(body);
        setForm(EMPTY_CUSTOMER_FORM);
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const busy = create.isPending || update.isPending;

  return (
    <Dialog open={open} onClose={onClose} title={editingId ? "Edit customer" : "Add customer"}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}
        <Field label="Company name" htmlFor="company_name">
          <Input
            id="company_name"
            required
            value={form.company_name}
            onChange={(e) => set("company_name")(e.target.value)}
          />
        </Field>
        <Field label="Site" htmlFor="site_id">
          <Select
            id="site_id"
            value={form.site_id}
            onChange={(e) => set("site_id")(e.target.value)}
          >
            <option value="">Default Site</option>
            {(sites ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="City" htmlFor="city">
            <Input id="city" value={form.city} onChange={(e) => set("city")(e.target.value)} />
          </Field>
          <Field label="Phone" htmlFor="phone">
            <Input id="phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} />
          </Field>
        </div>
        <Field label="Email" htmlFor="cust_email">
          <Input
            id="cust_email"
            type="email"
            value={form.email}
            onChange={(e) => set("email")(e.target.value)}
          />
        </Field>
        {canReadUsers && (
          <Field label="Account manager" htmlFor="account_manager_id">
            <Select
              id="account_manager_id"
              value={form.account_manager_id}
              onChange={(e) => set("account_manager_id")(e.target.value)}
            >
              <option value="">— unassigned —</option>
              {managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.full_name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="GSTIN (optional)" htmlFor="gst_number">
            <Input
              id="gst_number"
              placeholder="27AAPFU0939F1ZV"
              maxLength={15}
              className="font-mono uppercase"
              value={form.gst_number}
              onChange={(e) => set("gst_number")(e.target.value.toUpperCase())}
            />
          </Field>
          <Field label="Customer name" htmlFor="contact_name">
            <Input
              id="contact_name"
              value={form.contact_name}
              onChange={(e) => set("contact_name")(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Service contract type" htmlFor="scv_type">
            <Select
              id="scv_type"
              value={form.service_contract_type}
              onChange={(e) => set("service_contract_type")(e.target.value)}
            >
              <option value="">— select —</option>
              {Object.entries(SERVICE_CONTRACT_TYPE_LABELS).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Service status" htmlFor="scv_status">
            <Select
              id="scv_status"
              value={form.service_status}
              onChange={(e) => set("service_status")(e.target.value)}
            >
              <option value="">— select —</option>
              {Object.entries(SERVICE_STATUS_LABELS).map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Contract start date" htmlFor="scv_start">
            <Input
              id="scv_start"
              type="date"
              value={form.service_contract_start_date}
              onChange={(e) => set("service_contract_start_date")(e.target.value)}
            />
          </Field>
          <Field label="Contract expiry date" htmlFor="scv_expiry">
            <Input
              id="scv_expiry"
              type="date"
              value={form.service_contract_expiry_date}
              onChange={(e) => set("service_contract_expiry_date")(e.target.value)}
            />
          </Field>
          <Field label="AMC expiry (legacy)" htmlFor="amc_expiry_date">
            <Input
              id="amc_expiry_date"
              type="date"
              value={form.amc_expiry_date}
              onChange={(e) => set("amc_expiry_date")(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy || !form.company_name.trim()}>
            {busy ? "Saving…" : editingId ? "Save changes" : "Create customer"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function CustomerDetailDialog({
  customerId,
  onClose,
}: {
  customerId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useCustomer(customerId);
  const addLocation = useAddLocation();
  const addContact = useAddContact();
  const deleteContact = useDeleteContact();
  const [locationName, setLocationName] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", designation: "" });
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  return (
    <Dialog
      open={customerId !== null}
      onClose={onClose}
      title={data?.company_name ?? "Customer"}
    >
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {data && (
        <div className="space-y-5 text-sm">
          {error && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive"
            >
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Detail label="Code" value={data.code} />
            <Detail label="GSTIN" value={data.gst_number ?? "—"} mono />
            <Detail label="City" value={data.city ?? "—"} />
            <Detail label="Phone" value={data.phone ?? "—"} />
            <Detail label="Account manager" value={data.account_manager_name ?? "Unassigned"} />
            <Detail
              label="Service status"
              value={data.service_status ? SERVICE_STATUS_LABELS[data.service_status] : "—"}
            />
            <Detail
              label="Contract type"
              value={
                data.service_contract_type
                  ? SERVICE_CONTRACT_TYPE_LABELS[data.service_contract_type]
                  : "—"
              }
            />
            <Detail
              label="Contract expiry"
              value={data.service_contract_expiry_date ?? data.amc_expiry_date ?? "—"}
            />
          </div>

          <section>
            <h3 className="mb-2 flex items-center gap-1.5 font-medium">
              <MapPin className="h-4 w-4" aria-hidden="true" />
              Offices / Locations ({data.locations.length})
            </h3>
            <ul className="space-y-1">
              {data.locations.length === 0 && (
                <li className="text-muted-foreground">No offices recorded.</li>
              )}
              {data.locations.map((loc) => (
                <li key={loc.id} className="flex items-center justify-between gap-2">
                  <span>
                    {loc.name}
                    {loc.city ? <span className="text-muted-foreground"> · {loc.city}</span> : null}
                  </span>
                  {loc.is_primary && <Badge tone="success">Primary</Badge>}
                </li>
              ))}
            </ul>
            <RequirePermission permission="customer:update">
              <div className="mt-2 flex gap-2">
                <Input
                  aria-label="New office name"
                  placeholder="Add an office / location"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                />
                <Button
                  variant="outline"
                  disabled={!locationName.trim() || addLocation.isPending}
                  onClick={() =>
                    void run(async () => {
                      await addLocation.mutateAsync({
                        customerId: data.id,
                        body: { name: locationName.trim() },
                      });
                      setLocationName("");
                    })
                  }
                >
                  Add
                </Button>
              </div>
            </RequirePermission>
          </section>

          <section>
            <h3 className="mb-2 flex items-center gap-1.5 font-medium">
              <Building2 className="h-4 w-4" aria-hidden="true" />
              Contacts ({data.contacts.length})
            </h3>
            <ul className="space-y-1.5">
              {data.contacts.length === 0 && (
                <li className="text-muted-foreground">No contacts recorded.</li>
              )}
              {data.contacts.map((c) => (
                <li
                  key={c.id}
                  className="flex items-start justify-between gap-2 rounded-md border border-border px-2.5 py-1.5"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium">{c.name}</span>
                      {c.designation && (
                        <span className="text-xs text-muted-foreground">· {c.designation}</span>
                      )}
                      {c.is_primary && <Badge tone="success">Primary</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {c.email && <span>{c.email}</span>}
                      {c.email && c.phone && <span> · </span>}
                      {c.phone && <span>{c.phone}</span>}
                      {!c.email && !c.phone && <span>No email or phone</span>}
                    </div>
                  </div>
                  <RequirePermission permission="customer:update">
                    <ConfirmButton
                      aria-label={`Remove contact ${c.name}`}
                      className="shrink-0"
                      title="Remove contact?"
                      confirmLabel="Remove"
                      message={
                        <>Remove contact <span className="font-medium text-foreground">{c.name}</span>?</>
                      }
                      onConfirm={() => run(() => deleteContact.mutateAsync(c.id))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </ConfirmButton>
                  </RequirePermission>
                </li>
              ))}
            </ul>
            <RequirePermission permission="customer:update">
              <div className="mt-3 space-y-2 rounded-md border border-dashed border-border p-2.5">
                <p className="text-xs font-medium text-muted-foreground">Add a contact person</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Input
                    aria-label="Contact name"
                    placeholder="Name"
                    value={contact.name}
                    onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                  />
                  <Input
                    aria-label="Contact designation"
                    placeholder="Designation (optional)"
                    value={contact.designation}
                    onChange={(e) => setContact((c) => ({ ...c, designation: e.target.value }))}
                  />
                  <Input
                    type="email"
                    aria-label="Contact email"
                    placeholder="Email"
                    value={contact.email}
                    onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                  />
                  <Input
                    aria-label="Contact mobile number"
                    placeholder="Mobile number"
                    value={contact.phone}
                    onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    disabled={!contact.name.trim() || addContact.isPending}
                    onClick={() =>
                      void run(async () => {
                        await addContact.mutateAsync({
                          customerId: data.id,
                          body: {
                            name: contact.name.trim(),
                            email: contact.email.trim() || null,
                            phone: contact.phone.trim() || null,
                            designation: contact.designation.trim() || null,
                          },
                        });
                        setContact({ name: "", email: "", phone: "", designation: "" });
                      })
                    }
                  >
                    Add contact
                  </Button>
                </div>
              </div>
            </RequirePermission>
          </section>

          <PortalUsersSection customerId={data.id} locations={data.locations} />

          {data.notes && (
            <section>
              <h3 className="mb-1 font-medium">Notes</h3>
              <p className="whitespace-pre-wrap text-muted-foreground">{data.notes}</p>
            </section>
          )}
        </div>
      )}
    </Dialog>
  );
}

function PortalUsersSection({
  customerId,
  locations,
}: {
  customerId: string;
  locations: CustomerLocation[];
}) {
  const { can } = useAuth();
  const canManage = can("customer:portal:manage");
  const { data: users } = usePortalUsers(customerId);
  const create = useCreatePortalUser(customerId);
  const setActive = useUpdatePortalUser(customerId);
  const resetPw = useResetPortalUserPassword(customerId);
  const remove = useDeletePortalUser(customerId);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    see_all: false,
    location_id: "",
  });
  const [created, setCreated] = useState<PortalUserCreated | null>(null);
  // Temp password from a reset, keyed by user id so it shows on the right row.
  const [resetNotice, setResetNotice] = useState<{ id: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function toggleActive(userId: string, isActive: boolean) {
    setError(null);
    try {
      await setActive.mutateAsync({ userId, is_active: !isActive });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the login.");
    }
  }

  async function resetPassword(userId: string) {
    setError(null);
    setResetNotice(null);
    try {
      const r = await resetPw.mutateAsync(userId);
      setResetNotice({ id: userId, password: r.temporary_password });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reset the password.");
    }
  }

  async function toggleSeeAll(userId: string, current: boolean) {
    setError(null);
    try {
      await setActive.mutateAsync({ userId, can_view_all_tickets: !current });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the login.");
    }
  }

  async function setLocation(userId: string, locationId: string) {
    setError(null);
    try {
      await setActive.mutateAsync({ userId, location_id: locationId || null });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the location.");
    }
  }

  async function add() {
    setError(null);
    setCreated(null);
    try {
      const result = await create.mutateAsync({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        can_view_all_tickets: form.see_all,
        location_id: form.location_id || null,
      });
      setCreated(result);
      setForm({ full_name: "", email: "", phone: "", see_all: false, location_id: "" });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not add the portal user.");
    }
  }

  return (
    <section>
      <h3 className="mb-2 flex items-center gap-1.5 font-medium">
        <KeyRound className="h-4 w-4" aria-hidden="true" />
        Portal logins ({users?.length ?? 0})
      </h3>
      <p className="mb-2 text-xs text-muted-foreground">
        People at this customer who can sign in to the portal and log calls.
      </p>
      <ul className="space-y-1.5">
        {users?.length === 0 && (
          <li className="text-muted-foreground">No portal logins yet.</li>
        )}
        {users?.map((u) => (
          <li
            key={u.id}
            className="rounded-md border border-border px-2.5 py-1.5"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium">{u.full_name}</span>
                  {!u.is_active && <Badge tone="muted">Inactive</Badge>}
                  {u.can_view_all_tickets && <Badge tone="success">Sees all calls</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {u.email}
                  {u.last_login_at
                    ? ` · last login ${formatDateTime(u.last_login_at)}`
                    : " · never signed in"}
                </div>
                {canManage && locations.length > 0 ? (
                  <div className="mt-1.5 max-w-[240px]">
                    <Select
                      aria-label={`Location for ${u.full_name}`}
                      value={u.location_id ?? ""}
                      onChange={(e) => void setLocation(u.id, e.target.value)}
                    >
                      <option value="">— No location —</option>
                      {locations.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                          {l.is_primary ? " (primary)" : ""}
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : (
                  u.location_name && (
                    <div className="text-xs text-muted-foreground">📍 {u.location_name}</div>
                  )
                )}
              </div>
              {canManage && (
                <div className="flex shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    title={u.can_view_all_tickets ? "Restrict to own calls" : "Let this login see all calls"}
                    aria-label={
                      u.can_view_all_tickets
                        ? `Restrict ${u.full_name} to their own calls`
                        : `Let ${u.full_name} see all calls`
                    }
                    className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                    onClick={() => void toggleSeeAll(u.id, u.can_view_all_tickets)}
                  >
                    <Eye className={`h-4 w-4 ${u.can_view_all_tickets ? "text-sla-ok" : ""}`} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    title={u.is_active ? "Deactivate login" : "Activate login"}
                    aria-label={u.is_active ? `Deactivate ${u.full_name}` : `Activate ${u.full_name}`}
                    className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                    onClick={() => void toggleActive(u.id, u.is_active)}
                  >
                    <Power className={`h-4 w-4 ${u.is_active ? "text-sla-ok" : ""}`} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    title="Reset password"
                    aria-label={`Reset password for ${u.full_name}`}
                    className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                    onClick={() => void resetPassword(u.id)}
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <ConfirmButton
                    buttonVariant="ghost"
                    aria-label={`Remove login ${u.full_name}`}
                    title="Remove portal login?"
                    confirmLabel="Remove"
                    message={
                      <>Remove the portal login for <span className="font-medium text-foreground">{u.full_name}</span>? They will no longer be able to sign in.</>
                    }
                    onConfirm={async () => {
                      await remove.mutateAsync(u.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                  </ConfirmButton>
                </div>
              )}
            </div>
            {resetNotice?.id === u.id && (
              <p className="mt-1.5 rounded bg-sla-ok/5 px-2 py-1 text-xs text-muted-foreground">
                New temporary password (share securely):{" "}
                <span className="font-mono font-medium text-foreground">{resetNotice.password}</span>
              </p>
            )}
          </li>
        ))}
      </ul>

      {error && (
        <div role="alert" className="mt-2 rounded-md bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
          {error}
        </div>
      )}
      {created && (
        <div className="mt-2 rounded-md border border-sla-ok/40 bg-sla-ok/5 px-3 py-2 text-xs">
          <p className="font-medium text-foreground">
            {created.user.full_name} can now sign in with {created.user.email}.
          </p>
          <p className="mt-1 text-muted-foreground">
            Temporary password (shown once — share it securely):{" "}
            <span className="font-mono font-medium text-foreground">
              {created.temporary_password}
            </span>
          </p>
        </div>
      )}

      <RequirePermission permission="customer:portal:manage">
        <div className="mt-3 space-y-2 rounded-md border border-dashed border-border p-2.5">
          <p className="text-xs font-medium text-muted-foreground">Add a portal login</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <Input
              aria-label="Portal user name"
              placeholder="Full name"
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
            />
            <Input
              type="email"
              aria-label="Portal user email"
              placeholder="Email (their login)"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
            <Input
              aria-label="Portal user mobile number"
              placeholder="Mobile number (optional)"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
            {locations.length > 0 && (
              <Select
                aria-label="Portal user location"
                value={form.location_id}
                onChange={(e) => setForm((f) => ({ ...f, location_id: e.target.value }))}
              >
                <option value="">Location (optional)…</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                    {l.is_primary ? " (primary)" : ""}
                  </option>
                ))}
              </Select>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={form.see_all}
              onChange={(e) => setForm((f) => ({ ...f, see_all: e.target.checked }))}
            />
            Can see all of this customer&apos;s calls (a supervisor). Otherwise they
            see only the calls they log.
          </label>
          <div className="flex justify-end">
            <Button
              variant="outline"
              disabled={!form.full_name.trim() || !form.email.trim() || create.isPending}
              onClick={() => void add()}
            >
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              Add portal user
            </Button>
          </div>
        </div>
      </RequirePermission>
    </section>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={mono ? "font-mono text-xs" : ""}>{value}</div>
    </div>
  );
}
